import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connectSocket, disconnectSocket, getSocket, isSocketConnected, onSocketChange, ensureConnection, updateLastDataReceived } from '../socket';
import { App as CapacitorApp } from '@capacitor/app';
import Sidebar from './Sidebar';
import ChatWindow from './ChatWindow';
import ProjectView from './ProjectView';
import Settings from './Settings';
import FriendRequests from './FriendRequests';
import GroupSettings from './GroupSettings';
import './Chat.css';
import { setActiveRoomId } from '../utils/activeChat';
import { DEFAULT_BUBBLE_COLOR, isValidBubbleColor } from '../utils/bubbleColors';
import { debugLog } from '../utils/debug';

function Chat({ user }) {
  const [currentRoom, setCurrentRoom] = useState(null);
  const [rooms, setRooms] = useState(() => {
    try {
      const cached = localStorage.getItem('tchat_rooms_cache');
      if (!cached) return [];
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch (_) {
      return [];
    }
  });
  const [friends, setFriends] = useState([]);
  const friendsRef = useRef([]);
  const currentRoomRef = useRef(null);
  const currentProjectRef = useRef(null);
  const messagesRef = useRef({});
  const [messages, setMessages] = useState({});
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showFriendRequests, setShowFriendRequests] = useState(false);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [selectedGroupForSettings, setSelectedGroupForSettings] = useState(null);
  const [paginationByRoom, setPaginationByRoom] = useState({}); // roomId -> { loadingOlder, hasMoreOlder, hasMoreNewer }
  const [loadingMessagesByRoom, setLoadingMessagesByRoom] = useState({}); // roomId -> boolean
  // Pending "scroll to this message" request per room: roomId -> { messageId, at }
  const [jumpTargetByRoom, setJumpTargetByRoom] = useState({});
  const PAGE_SIZE = 50;
  const [myBubbleColor, setMyBubbleColor] = useState(() => {
    try {
      const saved = localStorage.getItem('tchat_bubble_color');
      return isValidBubbleColor(saved) ? saved : DEFAULT_BUBBLE_COLOR;
    } catch (_) {
      return DEFAULT_BUBBLE_COLOR;
    }
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [roomUnreadCounts, setRoomUnreadCounts] = useState({});
  const [soundNotificationsEnabled, setSoundNotificationsEnabled] = useState(() => {
    try {
      return localStorage.getItem('tchat_sound_notifications') !== 'false'; // Default to true
    } catch (_) {
      return true;
    }
  });

  // ── Project state ──────────────────────────────────────────────────
  const [sidebarTab, setSidebarTab] = useState('chats');
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  // Server-owned (pushed whenever anything changes): { projectId: total } and { projectId: { files, chat } }
  const [projectUnreadCounts, setProjectUnreadCounts] = useState({});
  const [projectUnreadDetail, setProjectUnreadDetail] = useState({});

  // Connect-on-chat mode: only keep the socket up while actively using chat features.
  // We treat these screens as requiring the socket:
  // - a room is open
  // - settings modal open (bubble color)
  // - friend requests modal open
  // - group settings open
  const disconnectTimerRef = useRef(null);
  // Used to prevent disconnect races during the first interaction after reload.
  const pendingSocketUseRef = useRef(false);
  // Prevent duplicate room history fetches (they cause scroll jumps).
  const lastRoomMessagesGetAtRef = useRef({}); // roomId -> ms timestamp
  // Track if we've done the initial rooms fetch on page load
  const hasInitialRoomsFetchRef = useRef(false);
  // Track last sync timestamp for staleness detection
  const lastSyncAtRef = useRef(Date.now());
  // Track if we're currently resuming from background
  const isResumingRef = useRef(false);
  // Track pending once('connect') handler to cancel on rapid room switches
  const pendingConnectHandlerRef = useRef(null);

  const requestRoomMessages = useCallback((socket, roomId, { reason = 'unknown', force = false } = {}) => {
    if (!socket || !roomId) return false;
    const now = Date.now();
    const last = lastRoomMessagesGetAtRef.current[roomId] || 0;
    // Debounce duplicate fetches (they cause scroll jumps) — but never let the debounce
    // suppress an explicit/forced request, e.g. the user just opened this room.
    if (!force && now - last < 1500) return false;

    setLoadingMessagesByRoom((prev) => ({ ...prev, [roomId]: true }));
    try {
      socket.emit('room:messages:get', { roomId });
      // Stamp the debounce timestamp ONLY after a successful emit, so a competing trigger
      // that merely stamped (without sending) can't make the real fetch a silent no-op
      // and leave the chat stuck on its loading spinner.
      lastRoomMessagesGetAtRef.current[roomId] = now;
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  // Mirror of loadingMessagesByRoom for use inside timers/handlers without stale closures.
  const loadingMessagesByRoomRef = useRef({});
  useEffect(() => { loadingMessagesByRoomRef.current = loadingMessagesByRoom; }, [loadingMessagesByRoom]);

  // Mirror of paginationByRoom, so socket handlers can tell whether a room is currently
  // showing a historical window (a "gap" between what's loaded and the newest message).
  const paginationByRoomRef = useRef({});
  useEffect(() => { paginationByRoomRef.current = paginationByRoom; }, [paginationByRoom]);

  // Watchdog so a room can never get stuck on the loading spinner if room:messages never
  // arrives (lost emit after a forceNew reconnect, dropped packet, etc.). roomId -> timers.
  const messagesLoadWatchdogRef = useRef({});
  const clearMessagesWatchdog = useCallback((roomId) => {
    const w = messagesLoadWatchdogRef.current[roomId];
    if (w) {
      clearTimeout(w.retryTimer);
      clearTimeout(w.giveUpTimer);
      delete messagesLoadWatchdogRef.current[roomId];
    }
  }, []);
  const armMessagesWatchdog = useCallback((roomId) => {
    clearMessagesWatchdog(roomId);
    const retryTimer = setTimeout(() => {
      if (!loadingMessagesByRoomRef.current[roomId]) { clearMessagesWatchdog(roomId); return; }
      const s = getSocket();
      if (s && isSocketConnected()) {
        requestRoomMessages(s, roomId, { reason: 'load-watchdog-retry', force: true });
      }
    }, 4000);
    const giveUpTimer = setTimeout(() => {
      // Still loading after the retry window — stop the infinite spinner so the user sees
      // whatever is cached (and can pull/reopen) instead of a dead screen.
      if (loadingMessagesByRoomRef.current[roomId]) {
        setLoadingMessagesByRoom((prev) => ({ ...prev, [roomId]: false }));
      }
      clearMessagesWatchdog(roomId);
    }, 10000);
    messagesLoadWatchdogRef.current[roomId] = { retryTimer, giveUpTimer };
  }, [requestRoomMessages, clearMessagesWatchdog]);

  // Clear any pending load watchdogs on unmount so timers can't fire after teardown.
  useEffect(() => {
    const watchdogs = messagesLoadWatchdogRef.current;
    return () => {
      Object.keys(watchdogs).forEach((rid) => {
        clearTimeout(watchdogs[rid].retryTimer);
        clearTimeout(watchdogs[rid].giveUpTimer);
      });
    };
  }, []);
  useEffect(() => {
    const needsSocket = !!(currentRoom || currentProject || showSettings || showFriendRequests || showGroupSettings);
    const needsInitialFetch = !hasInitialRoomsFetchRef.current;
    let cancelled = false;

    const ensureConnected = async () => {
      if (isSocketConnected()) return;
      try {
        const token = await user.getIdToken();
        if (cancelled) return;
        connectSocket(token);
        if (needsInitialFetch) {
          hasInitialRoomsFetchRef.current = true;
        }
      } catch (_) {
        // If we can't connect, UI actions will fail gracefully elsewhere.
      }
    };

    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }

    if (needsSocket || needsInitialFetch) {
      pendingSocketUseRef.current = true;
      ensureConnected();
    } else {
      pendingSocketUseRef.current = false;
      // Small delay prevents rapid connect/disconnect when switching views.
      disconnectTimerRef.current = setTimeout(() => {
        // If user is in the middle of opening a chat, don't disconnect mid-handshake.
        if (pendingSocketUseRef.current) return;
        disconnectSocket();
      }, 600);
    }

    return () => {
      cancelled = true;
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
    };
  }, [user, currentRoom, currentProject, showSettings, showFriendRequests, showGroupSettings]);

  // Audio context for notification sounds
  const audioContextRef = useRef(null);

  // Desktop: allow collapsing the left sidebar to an icon rail.
  const isMobile = () => window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

  // Calculate unread messages count
  const calculateUnreadCount = useCallback((allMessages, currentRoomId) => {
    let count = 0;
    Object.entries(allMessages).forEach(([roomId, roomMessages]) => {
      // Skip counting unread messages for the currently open room
      if (roomId === currentRoomId) return;

      roomMessages.forEach(message => {
        // Count messages not sent by current user and not seen by current user
        if (message.senderId !== user.uid && !message.seenBy?.includes(user.uid)) {
          count++;
        }
      });
    });
    return count;
  }, [user.uid]);

  // Calculate per-room unread message counts
  const calculatePerRoomUnreadCounts = useCallback((allMessages, currentRoomId) => {
    const counts = {};
    Object.entries(allMessages).forEach(([roomId, roomMessages]) => {
      // Skip counting unread messages for the currently open room
      if (roomId === currentRoomId) return;

      let count = 0;
      roomMessages.forEach(message => {
        // Count messages not sent by current user and not seen by current user
        if (message.senderId !== user.uid && !message.seenBy?.includes(user.uid)) {
          count++;
        }
      });
      if (count > 0) {
        counts[roomId] = count;
      }
    });
    return counts;
  }, [user.uid]);

  // Initialize audio context on user interaction
  const initializeAudioContext = useCallback(async () => {
    try {
      if (!audioContextRef.current) {
        debugLog('Initializing audio context');
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      if (audioContextRef.current.state === 'suspended') {
        debugLog('Resuming audio context');
        await audioContextRef.current.resume();
      }

      return audioContextRef.current;
    } catch (error) {
      console.warn('❌ Audio context initialization failed:', error);
      return null;
    }
  }, []);

  // Play a notification sound (like Facebook)
  const playNotificationSound = useCallback(async () => {
    try {
      // Only play if audio context is available, document is not visible, and sound notifications are enabled
      if (!document.hidden || !soundNotificationsEnabled) return;

      debugLog('Attempting to play notification sound');

      const audioContext = await initializeAudioContext();
      if (!audioContext) return;

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Lower pitch notification sound
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(300, audioContext.currentTime + 0.1);

      gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.1, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);

      debugLog('Notification sound played successfully');
    } catch (error) {
      console.warn('❌ Notification sound failed:', error);
    }
  }, [initializeAudioContext, soundNotificationsEnabled]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('tchat_sidebar_collapsed') === '1';
    } catch (_) {
      return false;
    }
  });

  // Never keep the desktop sidebar collapsed on mobile layouts.
  useEffect(() => {
    const onResize = () => {
      if (isMobile()) {
        setIsSidebarCollapsed(false);
      }
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleSidebarCollapsed = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('tchat_sidebar_collapsed', next ? '1' : '0');
      } catch (_) {}
      return next;
    });
  };

  // Handle opening chat from notification
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chatToOpen = urlParams.get('openChat');

    if (chatToOpen && rooms.length > 0) {
      const room = rooms.find(r => r.roomId === chatToOpen);
      if (room) {
        // Use handleSelectRoom which properly handles socket connection lifecycle
        handleSelectRoom(room);

        // Clear the URL parameter
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms]);

  // Handle Android back button
  useEffect(() => {
    const handleBackButton = (event) => {
      console.log('📱 Handling Android back button in Chat component', event?.detail);

      // If settings are open, close them first
      if (showSettings) {
        console.log('📱 Closing settings modal');
        setShowSettings(false);
        return true; // Indicate we handled it
      }

      // If friend requests are open, close them
      if (showFriendRequests) {
        console.log('📱 Closing friend requests modal');
        setShowFriendRequests(false);
        return true; // Indicate we handled it
      }

      // If a chat is open and sidebar is hidden (mobile), go back to sidebar
      if (currentRoom && !isMobileSidebarOpen) {
        console.log('📱 Going back to sidebar from chat');
        setIsMobileSidebarOpen(true);
        setCurrentRoom(null);
        return true; // Indicate we handled it
      }

      // If we're in the sidebar view, let the system handle it (exit app or minimize)
      if (isMobileSidebarOpen && !currentRoom) {
        console.log('📱 At root level, letting system handle back button');
        return false; // Let system handle it
      }

      return true; // Default to handling it
    };

    // Use capture phase to ensure we catch the event
    const backButtonHandler = (event) => {
      const handled = handleBackButton(event);
      if (handled) {
        // Prevent the default behavior if we handled it
        event.preventDefault?.();
        event.stopPropagation?.();
      }
    };

    // Register both custom event and direct Capacitor listener
    window.addEventListener('androidBackButton', backButtonHandler, true);

    // Also register direct Capacitor back button listener
    let capacitorBackButtonListener = null;
    const setupCapacitorListener = async () => {
      try {
        capacitorBackButtonListener = await CapacitorApp.addListener('backButton', (event) => {
          console.log('📱 Direct Capacitor back button in Chat component');
          const handled = handleBackButton(event);
          if (handled) {
            event.preventDefault?.();
            event.stopPropagation?.();
          }
        });
        console.log('📱 Direct Capacitor back button listener registered in Chat');
      } catch (error) {
        console.error('❌ Failed to register direct Capacitor back button listener in Chat:', error);
      }
    };

    setupCapacitorListener();

    // Also expose a global function for direct back button handling
    window.handleAndroidBackButton = handleBackButton;

    return () => {
      window.removeEventListener('androidBackButton', backButtonHandler, true);
      if (capacitorBackButtonListener) {
        capacitorBackButtonListener.remove();
      }
      delete window.handleAndroidBackButton;
    };
  }, [currentRoom, isMobileSidebarOpen, showSettings, showFriendRequests]);

  // Keep a ref with the latest friends list for socket handlers
  useEffect(() => {
    friendsRef.current = friends;
  }, [friends]);

  // Keep latest messages for socket handlers
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Sort rooms by most recent message timestamp (newest first)
  const sortedRooms = React.useMemo(() => {
    return [...rooms].sort((a, b) => {
      const aTime = a.lastMessage?.timestamp || 0;
      const bTime = b.lastMessage?.timestamp || 0;
      
      // Handle both unix timestamps (ms) and date strings
      const aMs = typeof aTime === 'number' ? aTime : new Date(aTime).getTime();
      const bMs = typeof bTime === 'number' ? bTime : new Date(bTime).getTime();
      
      return bMs - aMs; // Most recent first
    });
  }, [rooms]);

  // Robust visibility/resume handler - reconnect and refresh all data
  // Also disconnects socket after 5 minutes of tab being hidden to reduce Cloud Run costs
  useEffect(() => {
    let resumeDebounceTimer = null;
    let idleDisconnectTimer = null; // Disconnect socket after prolonged tab hide
    const IDLE_DISCONNECT_MS = 5 * 60 * 1000; // 5 minutes

    const handleResume = async () => {
      // Prevent duplicate handling
      if (isResumingRef.current) return;
      isResumingRef.current = true;
      
      debugLog('🔄 App resumed/visible - checking connection and refreshing data...');
      
      try {
        // Get fresh token and ensure connection
        const token = await user.getIdToken();
        const socket = await ensureConnection(token);
        
        if (!socket) {
          debugLog('🔄 Could not establish socket connection on resume');
          isResumingRef.current = false;
          return;
        }
        
        // Wait for socket to be fully connected
        const waitForConnection = () => new Promise((resolve) => {
          if (socket.connected) {
            resolve();
            return;
          }
          const timeout = setTimeout(() => {
            socket.off('connect', onConnect);
            resolve();
          }, 5000);
          const onConnect = () => {
            clearTimeout(timeout);
            socket.off('connect', onConnect);
            resolve();
          };
          socket.once('connect', onConnect);
        });
        
        await waitForConnection();
        
        if (!socket.connected) {
          debugLog('🔄 Socket connection timeout on resume');
          isResumingRef.current = false;
          return;
        }
        
        debugLog('🔄 Socket connected after resume');

        // Server auto-pushes rooms:list and friends:list on new connections,
        // so we only need to refresh the active room's messages if data is stale
        const staleness = Date.now() - (lastSyncAtRef.current || 0);
        if (currentRoom?.roomId && staleness > 30000) {
          debugLog('🔄 Refreshing current room messages (stale %dms):', staleness, currentRoom.roomId);
          socket.emit('room:messages:get', { roomId: currentRoom.roomId });
        }

        lastSyncAtRef.current = Date.now();
        
      } catch (error) {
        console.error('🔄 Error during resume:', error);
      }
      
      // Reset resuming flag after a delay
      setTimeout(() => {
        isResumingRef.current = false;
      }, 2000);
    };
    
    const handleVisibility = () => {
      if (document.hidden) {
        // Clear any pending resume
        if (resumeDebounceTimer) {
          clearTimeout(resumeDebounceTimer);
          resumeDebounceTimer = null;
        }
        // Start idle disconnect timer - disconnect socket after 5 min hidden
        if (!idleDisconnectTimer) {
          idleDisconnectTimer = setTimeout(() => {
            debugLog('💤 Tab hidden for 5 min, disconnecting socket to save resources...');
            disconnectSocket();
            idleDisconnectTimer = null;
          }, IDLE_DISCONNECT_MS);
        }
        return;
      }

      // Tab became visible - cancel idle disconnect if pending
      if (idleDisconnectTimer) {
        clearTimeout(idleDisconnectTimer);
        idleDisconnectTimer = null;
      }

      // Debounce resume to avoid rapid-fire on quick lock/unlock
      if (resumeDebounceTimer) {
        clearTimeout(resumeDebounceTimer);
      }
      resumeDebounceTimer = setTimeout(handleResume, 100);
    };
    
    // Handle Capacitor app state changes (mobile)
    let appStateListener = null;
    const isNative = typeof window !== 'undefined' && 
      window.Capacitor && 
      typeof window.Capacitor.getPlatform === 'function' && 
      window.Capacitor.getPlatform() !== 'web';
    
    const setupAppStateListener = async () => {
      if (!isNative) return;
      
      try {
        appStateListener = await CapacitorApp.addListener('appStateChange', (state) => {
          if (state.isActive) {
            debugLog('📱 Capacitor: App became active');
            // Debounce resume
            if (resumeDebounceTimer) {
              clearTimeout(resumeDebounceTimer);
            }
            resumeDebounceTimer = setTimeout(handleResume, 100);
          }
        });
        debugLog('📱 App state listener registered');
      } catch (error) {
        console.error('📱 Failed to register app state listener:', error);
      }
    };
    
    setupAppStateListener();
    document.addEventListener('visibilitychange', handleVisibility);
    
    // Also handle window focus (for web)
    const handleFocus = () => {
      if (!document.hidden) {
        if (resumeDebounceTimer) {
          clearTimeout(resumeDebounceTimer);
        }
        resumeDebounceTimer = setTimeout(handleResume, 100);
      }
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      if (resumeDebounceTimer) {
        clearTimeout(resumeDebounceTimer);
      }
      if (idleDisconnectTimer) {
        clearTimeout(idleDisconnectTimer);
      }
      if (appStateListener) {
        appStateListener.remove();
      }
    };
  }, [user, currentRoom]);

  // Update document title with unread message count
  useEffect(() => {
    const baseTitle = 'tChat';
    document.title = unreadCount > 0 ? `${baseTitle} (${unreadCount})` : baseTitle;
  }, [unreadCount]);

  // Periodic connection health check (every 60s while visible)
  useEffect(() => {
    let healthCheckInterval = null;
    let hiddenSinceTs = null; // Track when tab was hidden

    const checkHealthAndRefresh = async () => {
      // Only check if document is visible
      if (document.hidden) return;

      const socket = getSocket();

      // Check if socket is disconnected or seems stale
      if (!socket || !isSocketConnected()) {
        debugLog('🔄 Health check: Socket disconnected, attempting reconnect...');
        try {
          const token = await user.getIdToken();
          await ensureConnection(token);
        } catch (error) {
          console.error('🔄 Health check reconnect failed:', error);
        }
        return;
      }

      // Check data staleness - if no data received in 5 min and we're supposed to be connected
      // (server pushes updates via Socket.IO events, so this is just a safety net)
      const staleness = Date.now() - lastSyncAtRef.current;
      if (staleness > 300000) {
        debugLog('🔄 Health check: Data stale (', staleness, 'ms), refreshing rooms...');
        socket.emit('rooms:get');
      }
    };

    const startHealthCheck = () => {
      // Clear any existing interval
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
      }

      // Check immediately on start
      checkHealthAndRefresh();

      // Then check every 2 minutes (connections are stable with fixed heartbeat timing)
      healthCheckInterval = setInterval(checkHealthAndRefresh, 120000);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        hiddenSinceTs = Date.now();
        // Stop health check when hidden
        if (healthCheckInterval) {
          clearInterval(healthCheckInterval);
          healthCheckInterval = null;
        }
      } else {
        const wasHiddenFor = hiddenSinceTs ? Date.now() - hiddenSinceTs : 0;
        hiddenSinceTs = null;

        // If tab was hidden for >5 minutes, the socket was disconnected (see tab-idle logic).
        // The resume handler will take care of reconnecting, so just restart health check.
        if (wasHiddenFor > 5 * 60 * 1000) {
          debugLog('🔄 Tab was hidden for', Math.round(wasHiddenFor / 1000), 's, deferring to resume handler');
        }

        // Resume health check when visible
        startHealthCheck();
      }
    };

    // Start if document is visible
    if (!document.hidden) {
      startHealthCheck();
    }

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, currentRoom]);

  // Recalculate unread count whenever messages change
  useEffect(() => {
    const newUnreadCount = calculateUnreadCount(messages, currentRoom?.roomId);
    setUnreadCount(newUnreadCount);
    const newRoomUnreadCounts = calculatePerRoomUnreadCounts(messages, currentRoom?.roomId);
    setRoomUnreadCounts(newRoomUnreadCounts);
  }, [messages, currentRoom?.roomId, calculateUnreadCount, calculatePerRoomUnreadCounts]);

  // Initialize audio context on first user interaction
  useEffect(() => {
    const handleUserInteraction = async () => {
      await initializeAudioContext();
      // Remove listeners after first interaction
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, [initializeAudioContext]);

  // Track which room is active to mute notifications for that chat
  useEffect(() => {
    currentRoomRef.current = currentRoom;
    setActiveRoomId(currentRoom?.roomId || null);
  }, [currentRoom]);

  // Keep a ref to the open project so socket handlers can read it without stale closures.
  useEffect(() => {
    currentProjectRef.current = currentProject;
  }, [currentProject]);

  useEffect(() => {
    let cleanupCurrentSocket = null;
    let roomsRefreshInterval = null;
    let offlineHandlerRef = null;
    
    const setupSocketListeners = (socket) => {
      // Clean up previous socket listeners if any
      if (cleanupCurrentSocket) {
        cleanupCurrentSocket();
        cleanupCurrentSocket = null;
      }
      
      if (!socket) {
        console.error('❌ No socket available in Chat component');
        return;
      }

    // On reconnect, server auto-pushes rooms:list, friends:list, friend_requests:list
    // so we only need to refresh the active room's messages (if any)
    socket.on('connect', () => {
      debugLog('🔌 Socket connected in Chat component');
      updateLastDataReceived();

      // Only refresh messages for the currently open room
      const activeRoom = currentRoomRef.current;
      if (activeRoom?.roomId) {
        requestRoomMessages(socket, activeRoom.roomId, { reason: 'socket-connect' });
      }
    });

    // Listen for user info
    socket.on('user:info', (userInfo) => {
      try {
        const key = userInfo?.bubbleColor;
        if (isValidBubbleColor(key)) {
          setMyBubbleColor(key);
          try {
            localStorage.setItem('tchat_bubble_color', key);
          } catch (_) {}
        }
      } catch (_) {}
    });

    // Listen for friends list and enrich direct room display (name/photo)
    socket.on('friends:list', (friendsList) => {
      lastSyncAtRef.current = Date.now();
      setFriends(friendsList);
      setRooms((prev) => prev.map((room) => {
        if (room.type !== 'direct') return room;
        const otherUserId = room.members?.find(id => id !== user.uid);
        if (!otherUserId) return room;
        const friend = friendsList.find(f => f.userId === otherUserId);
        if (!friend) return room;
        const updated = {
          ...room,
          name: friend.username || room.name || 'Direct Message',
          otherUserPhoto: friend.photoURL || room.otherUserPhoto || null
        };
        return updated;
      }));
    });

    // Friend changed bubble color (keep local friend list fresh)
    socket.on('friend:bubbleColor:updated', ({ userId, bubbleColor }) => {
      if (!userId || !isValidBubbleColor(bubbleColor)) return;
      setFriends((prev) => prev.map((f) => (f.userId === userId ? { ...f, bubbleColor } : f)));
    });

    // Listen for friend coming online
    socket.on('friend:online', (friend) => {
      // Update friends state
      setFriends((prev) => 
        prev.map(f => f.userId === friend.userId ? { ...f, ...friend, online: true } : f)
      );
      
      // Update rooms to reflect online status
      setRooms((prev) => prev.map(room => {
        if (room.type !== 'direct') return room;
        const otherUserId = room.members?.find(id => id !== user.uid);
        if (otherUserId === friend.userId) {
          return { ...room, online: true };
        }
        return room;
      }));
    });

    // Listen for friend going offline
    offlineHandlerRef = (data) => {
      const { userId, lastSeen, platform } = data || {};

      if (!userId) return;

      // Update friends state (keep last-seen so we can show "last seen Xm ago")
      setFriends((prev) =>
        prev.map(f => f.userId === userId
          ? { ...f, online: false, lastSeen: lastSeen || f.lastSeen || null, lastSeenPlatform: platform || f.lastSeenPlatform || null }
          : f)
      );
      
      // Update rooms to reflect offline status
      setRooms((prev) => prev.map(room => {
        if (room.type !== 'direct') return room;
        const otherUserId = room.members?.find(id => id !== user.uid);
        if (otherUserId === userId) {
          return { ...room, online: false };
        }
        return room;
      }));
    };
    socket.on('friend:offline', offlineHandlerRef);

    // Listen for batch online status update
    socket.on('friends:online:batch', (onlineFriends) => {
      
      setFriends((prev) => {
        const updated = [...prev];
        onlineFriends.forEach(onlineFriend => {
          const index = updated.findIndex(f => f.userId === onlineFriend.userId);
          if (index >= 0) {
            updated[index] = { ...updated[index], ...onlineFriend, online: onlineFriend.online };
          }
        });
        return updated;
      });
      
      // Update rooms with batch online status
      setRooms((prev) => prev.map(room => {
        if (room.type !== 'direct') return room;
        const otherUserId = room.members?.find(id => id !== user.uid);
        if (!otherUserId) return room;
        const friendStatus = onlineFriends.find(f => f.userId === otherUserId);
        if (friendStatus) {
          return { ...room, online: friendStatus.online };
        }
        return room;
      }));
    });

    // Listen for friend request accepted
    socket.on('friend_request:accepted', ({ friend }) => {
      setFriends((prev) => {
        const existing = prev.find(f => f.userId === friend.userId);
        if (existing) return prev;
        return [...prev, friend];
      });
      
      // Auto-create direct chat with new friend
      socket.emit('room:join:direct', { recipientId: friend.userId });
    });

    // Listen for friend removed
    socket.on('friend:removed', ({ friendId }) => {
      setFriends((prev) => prev.filter(f => f.userId !== friendId));
    });

    // Listen for rooms list
    socket.on('rooms:list', (roomsList) => {
      updateLastDataReceived(); // Track data freshness
      lastSyncAtRef.current = Date.now();
      
      // Enrich direct rooms with friend name/photo/online status if available
      const enriched = (roomsList || []).map((room) => {
        if (room.type !== 'direct') return room;
        const otherUserId = room.members?.find(id => id !== user.uid);
        if (!otherUserId) return room;
        const friend = friendsRef.current.find(f => f.userId === otherUserId);
        if (!friend) return room;
        return {
          ...room,
          name: friend.username || room.name || 'Direct Message',
          otherUserPhoto: friend.photoURL || room.otherUserPhoto || null,
          online: friend.online
        };
      });
      // Treat a missing `archived` as "unchanged", not "false" — a payload that
      // omits the field must never pop an archived chat back into the list.
      // Unarchiving always sends an explicit false, so this can't get stuck.
      setRooms((prev) => {
        const merged = enriched.map((room) => (
          room.archived === undefined
            ? { ...room, archived: !!prev.find(p => p.roomId === room.roomId)?.archived }
            : room
        ));
        // Cache for fast native hydration
        try {
          localStorage.setItem('tchat_rooms_cache', JSON.stringify(merged));
        } catch (_) {}
        return merged;
      });

      // If viewing a room, ensure its messages are synced when a newer lastMessage is seen
      if (currentRoom?.roomId) {
        const latestRoom = enriched.find(r => r.roomId === currentRoom.roomId);
        if (latestRoom?.lastMessage) {
          const coerceToMillis = (t) => {
            if (!t) return 0;
            if (typeof t === 'number') return t > 1e12 ? t : t * 1000;
            const parsed = Date.parse(t);
            return Number.isNaN(parsed) ? 0 : parsed;
          };
          const remoteTs = coerceToMillis(latestRoom.lastMessage.timestamp);
          const localMessages = messagesRef.current[currentRoom.roomId] || [];
          const localTs = localMessages.length
            ? coerceToMillis(localMessages[localMessages.length - 1].timestamp)
            : 0;
          if (remoteTs > localTs) {
            requestRoomMessages(socket, currentRoom.roomId, { reason: 'rooms-list-newer-lastMessage' });
          }
        }
      }
      
      // Don't load message history for all rooms automatically - only load when user opens a chat
      // This significantly speeds up initial load time
      // enriched.forEach(room => {
      //   socket.emit('room:messages:get', { roomId: room.roomId });
      // });
    });

    // Do not auto-emit rooms:get here; server pushes rooms:list on connect.

    // ── Project socket listeners ──────────────────────────────────────
    socket.emit('projects:get');

    socket.on('projects:list', (projectsList) => {
      setProjects(projectsList || []);
    });

    socket.on('project:created', (project) => {
      setProjects(prev => {
        if (prev.some(p => p.projectId === project.projectId)) return prev;
        return [project, ...prev];
      });
    });

    socket.on('project:updated', (project) => {
      setProjects(prev => prev.map(p => p.projectId === project.projectId ? { ...p, ...project } : p));
      setCurrentProject(prev => prev?.projectId === project.projectId ? { ...prev, ...project } : prev);
    });

    socket.on('project:deleted', ({ projectId }) => {
      setProjects(prev => prev.filter(p => p.projectId !== projectId));
      setCurrentProject(prev => prev?.projectId === projectId ? null : prev);
    });

    socket.on('project:members:updated', ({ projectId, members }) => {
      setProjects(prev => prev.map(p => p.projectId === projectId ? { ...p, members } : p));
      setCurrentProject(prev => prev?.projectId === projectId ? { ...prev, members } : prev);
    });

    // ── Project unread tracking ──────────────────────────────────────
    // The server recounts and pushes these whenever a project gets a message, upload,
    // comment or seen-update, so there is no local incrementing to drift out of sync.
    socket.on('project:unread:counts', (counts) => {
      setProjectUnreadCounts(counts || {});
    });

    socket.on('project:unread:detail', (detail) => {
      setProjectUnreadDetail(detail || {});
    });

    // Ask again on every (re)connect: anything that arrived while we were offline is counted.
    const requestProjectUnread = () => socket.emit('project:unread:get');
    socket.on('connect', requestProjectUnread);
    if (socket.connected) requestProjectUnread();

    // Listen for new room joined
    socket.on('room:joined', (room) => {
      // Enrich direct rooms with friend name/photo/online status immediately
      let enrichedRoom = room;
      if (room.type === 'direct') {
        const otherUserId = room.members?.find(id => id !== user.uid);
        if (otherUserId) {
          const friend = friendsRef.current.find(f => f.userId === otherUserId);
          if (friend) {
            enrichedRoom = {
              ...room,
              name: friend.username || room.name || 'Direct Message',
              otherUserPhoto: friend.photoURL || room.otherUserPhoto || null,
              online: friend.online
            };
          }
        }
      }

      setRooms((prev) => {
        const existing = prev.find(r => r.roomId === enrichedRoom.roomId);
        if (existing) return prev;
        return [...prev, enrichedRoom];
      });
      // Only set messages if they're provided and not empty, otherwise request them
      if (enrichedRoom.messages && enrichedRoom.messages.length > 0) {
        setMessages((prev) => ({
          ...prev,
          [enrichedRoom.roomId]: enrichedRoom.messages
        }));
      } else {
        // Request message history if not provided
        requestRoomMessages(socket, enrichedRoom.roomId, { reason: 'room-joined-no-history' });
      }
    });

    // Listen for new messages
    socket.on('message:new', (message) => {
      updateLastDataReceived(); // Track data freshness
      lastSyncAtRef.current = Date.now();

      // If this room is currently showing a historical window (user jumped to a search
      // result), appending live messages would splice them onto the wrong point in
      // history. Skip the append — "Jump to latest" reloads the newest page.
      const isViewingHistory = paginationByRoomRef.current?.[message.roomId]?.hasMoreNewer === true;

      // If the user sent it themselves, snap the room back to live so they see it land.
      if (isViewingHistory && message.senderId === user.uid) {
        requestRoomMessages(socket, message.roomId, { reason: 'sent-while-viewing-history', force: true });
      }

      setMessages((prev) => {
        if (isViewingHistory) return prev;
        const existing = prev[message.roomId] || [];
        // Guard against duplicates from reconnect redelivery or a near-simultaneous
        // history refresh (the server also echoes the sender's own message directly).
        if (message.id && existing.some((m) => m.id === message.id)) return prev;
        return { ...prev, [message.roomId]: [...existing, message] };
      });

      // Update room's last message
      setRooms((prev) => prev.map(room =>
        room.roomId === message.roomId
          ? { ...room, lastMessage: message }
          : room
      ));

      // Play notification sound for messages from others (not current user)
      if (message.senderId !== user.uid) {
        playNotificationSound();
      }

      // Auto-mark as seen if user is currently viewing this chat room
      if (currentRoom?.roomId === message.roomId && message.senderId !== user.uid) {
        console.log('📖 Auto-marking new message as seen (user is in chat):', message.id);
        socket.emit('message:seen', {
          roomId: message.roomId,
          messageId: message.id
        });
      }
    });

    // Listen for message edits
    socket.on('message:edited', ({ id, content }) => {
      setMessages((prev) => {
        let targetRoomId = null;
        const updated = Object.fromEntries(
          Object.entries(prev).map(([rid, msgs]) => {
            const newMsgs = msgs.map(m => {
              if (m.id === id) {
                targetRoomId = rid;
                return { ...m, content };
              }
              return m;
            });
            return [rid, newMsgs];
          })
        );

        if (targetRoomId) {
          // If the edited message was lastMessage, update rooms
          setRooms((roomsPrev) => roomsPrev.map(r => {
            if (r.roomId !== targetRoomId) return r;
            if (r.lastMessage && r.lastMessage.id === id) {
              return { ...r, lastMessage: { ...r.lastMessage, content } };
            }
            return r;
          }));
        }

        return updated;
      });
    });

    // Listen for message deletes
    socket.on('message:deleted', ({ id }) => {
      setMessages((prev) => {
        let targetRoomId = null;
        let newLastMessage = null;
        const updated = Object.fromEntries(
          Object.entries(prev).map(([rid, msgs]) => {
            const filtered = msgs.filter(m => m.id !== id);
            if (filtered.length !== msgs.length) {
              targetRoomId = rid;
              newLastMessage = filtered[filtered.length - 1] || null;
            }
            return [rid, filtered];
          })
        );

        if (targetRoomId) {
          setRooms((roomsPrev) => roomsPrev.map(r => 
            r.roomId === targetRoomId ? { ...r, lastMessage: newLastMessage } : r
          ));
        }

        return updated;
      });
    });

    // Listen for placeholder messages (immediate display while uploading)
    socket.on('message:placeholder', (message) => {
      setMessages((prev) => ({
        ...prev,
        [message.roomId]: [...(prev[message.roomId] || []), message]
      }));
    });

    // Listen for placeholder removal (on upload error)
    socket.on('message:placeholder:removed', ({ roomId, placeholderId }) => {
      console.log('🗑️ Placeholder message removed:', { roomId, placeholderId });
      setMessages((prev) => ({
        ...prev,
        [roomId]: (prev[roomId] || []).filter(msg => msg.id !== placeholderId)
      }));
    });

    // Listen for placeholder replacement (when upload completes)
    socket.on('message:placeholder:replaced', ({ roomId, placeholderId, actualMessage }) => {
      console.log('🔄 Placeholder message replaced:', { roomId, placeholderId, actualMessage });
      setMessages((prev) => {
        const roomMessages = prev[roomId] || [];
        const hasPlaceholder = roomMessages.some(msg => msg.id === placeholderId);
        if (hasPlaceholder) {
          // Replace the placeholder with the actual message
          return { ...prev, [roomId]: roomMessages.map(msg => msg.id === placeholderId ? actualMessage : msg) };
        } else {
          // Placeholder was lost (e.g., socket reconnected during upload and messages refreshed from DB).
          // Check if the actual message already exists (avoid duplicates).
          const alreadyExists = roomMessages.some(msg => msg.id === actualMessage.id);
          if (alreadyExists) return prev;
          return { ...prev, [roomId]: [...roomMessages, actualMessage] };
        }
      });

      // Update room's last message
      setRooms((prev) => prev.map(room =>
        room.roomId === roomId ? { ...room, lastMessage: actualMessage } : room
      ));
    });

    // Listen for message seen updates
    socket.on('message:seen', (data) => {
      console.log('👁️ Received message seen update:', data);
      setMessages((prev) => {
        // Find which room contains this message
        let targetRoomId = null;
        for (const [roomId, roomMessages] of Object.entries(prev)) {
          if (roomMessages.some(msg => msg.id === data.messageId)) {
            targetRoomId = roomId;
            break;
          }
        }
        
        if (!targetRoomId) {
          console.log('👁️ Message not found in any room');
          return prev;
        }
        
        console.log('👁️ Updating message in room:', targetRoomId);
        
        // Create completely new objects to force React re-render
        const updatedMessages = (prev[targetRoomId] || []).map(msg => {
          if (msg.id === data.messageId) {
            const updated = { 
              ...msg, 
              seenBy: [...data.seenBy], 
              seenAt: {...data.seenAt} 
            };
            console.log('👁️ Updated message:', updated);
            return updated;
          }
          return msg;
        });
        
        return {
          ...prev,
          [targetRoomId]: updatedMessages
        };
      });
    });

    // Listen for message reaction updates
    socket.on('message:reaction', (data) => {
      console.log('👍 Received reaction update:', data);
      setMessages((prev) => {
        // Find which room contains this message
        let targetRoomId = null;
        for (const [roomId, roomMessages] of Object.entries(prev)) {
          if (roomMessages.some(msg => msg.id === data.messageId)) {
            targetRoomId = roomId;
            break;
          }
        }
        
        if (!targetRoomId) return prev;
        
        return {
          ...prev,
          [targetRoomId]: (prev[targetRoomId] || []).map(msg => 
            msg.id === data.messageId 
              ? { ...msg, reactions: data.reactions }
              : msg
          )
        };
      });
    });

    // Listen for room messages (history fetch)
    socket.on('room:messages', ({ roomId, messages: roomMessages }) => {
      updateLastDataReceived(); // Track data freshness
      lastSyncAtRef.current = Date.now();

      setMessages((prev) => ({
        ...prev,
        [roomId]: roomMessages || []
      }));

      // Keep sidebar lastMessage in sync with the actual newest message
      if (roomMessages && roomMessages.length > 0) {
        const newestMessage = roomMessages[roomMessages.length - 1];
        setRooms((prev) => prev.map(r =>
          r.roomId === roomId ? { ...r, lastMessage: newestMessage } : r
        ));
      }

      // History arrived — clear loading state and cancel the load watchdog for this room.
      clearMessagesWatchdog(roomId);
      setLoadingMessagesByRoom((prev) => {
        if (!roomId) return prev;
        if (!prev[roomId]) return prev;
        return { ...prev, [roomId]: false };
      });

      // Reset pagination flags for this room based on initial page size.
      // This is the newest page, so there is nothing newer to catch up to.
      setPaginationByRoom((prev) => ({
        ...prev,
        [roomId]: {
          loadingOlder: false,
          hasMoreOlder: (roomMessages || []).length >= PAGE_SIZE,
          hasMoreNewer: false
        }
      }));
    });

    // Listen for older message pages (pagination prepend)
    socket.on('room:messages:older', ({ roomId, messages: olderMessages, hasMore }) => {
      setMessages((prev) => {
        const current = prev[roomId] || [];
        const seen = new Set(current.map(m => m.id));
        const merged = [...(olderMessages || []).filter(m => !seen.has(m.id)), ...current];
        return { ...prev, [roomId]: merged };
      });
      setPaginationByRoom((prev) => ({
        ...prev,
        [roomId]: {
          ...(prev[roomId] || {}),
          loadingOlder: false,
          hasMoreOlder: !!hasMore
        }
      }));
    });

    // Listen for a window of messages around a jump target (search result / reply jump).
    // Replaces the room's loaded slice with that window and flags the gap to "now".
    socket.on('room:messages:around', ({ roomId, messageId, messages: windowMessages, found, hasMoreOlder, hasMoreNewer }) => {
      clearMessagesWatchdog(roomId);
      setLoadingMessagesByRoom((prev) => ({ ...prev, [roomId]: false }));

      if (!found || !windowMessages || windowMessages.length === 0) {
        // Message is gone (deleted, or access denied) — fall back to the newest page so
        // the room never lands on an empty screen.
        if (!(messagesRef.current?.[roomId] || []).length) {
          requestRoomMessages(socket, roomId, { reason: 'jump-target-missing', force: true });
        }
        return;
      }

      setMessages((prev) => ({ ...prev, [roomId]: windowMessages }));
      setPaginationByRoom((prev) => ({
        ...prev,
        [roomId]: {
          loadingOlder: false,
          loadingNewer: false,
          hasMoreOlder: !!hasMoreOlder,
          hasMoreNewer: !!hasMoreNewer
        }
      }));
      setJumpTargetByRoom((prev) => ({ ...prev, [roomId]: { messageId, at: Date.now() } }));
    });

    // Listen for newer message pages (append, used to catch back up after a jump)
    socket.on('room:messages:newer', ({ roomId, messages: newerMessages, hasMore }) => {
      setMessages((prev) => {
        const current = prev[roomId] || [];
        const seen = new Set(current.map(m => m.id));
        const merged = [...current, ...(newerMessages || []).filter(m => !seen.has(m.id))];
        return { ...prev, [roomId]: merged };
      });
      setPaginationByRoom((prev) => ({
        ...prev,
        [roomId]: {
          ...(prev[roomId] || {}),
          loadingNewer: false,
          hasMoreNewer: !!hasMore
        }
      }));
    });

    // Listen for group updates
    socket.on('room:updated', ({ roomId, name, photoURL, updatedBy }) => {
      console.log(`🔄 Room ${roomId} updated by ${updatedBy}`);
      // Update room in rooms list
      setRooms((prev) => prev.map(room => 
        room.roomId === roomId 
          ? { ...room, name, photoURL }
          : room
      ));
      // If the updated room is currently selected, update it
      if (currentRoom?.roomId === roomId) {
        setCurrentRoom((prev) => ({ ...prev, name, photoURL }));
      }
    });

    // Listen for chat archive/unarchive
    socket.on('room:archived', ({ roomId, archived }) => {
      setRooms((prev) => prev.map(room => room.roomId === roomId ? { ...room, archived } : room));
      // If the archived room is open, close it
      if (archived && currentRoom?.roomId === roomId) {
        setCurrentRoom(null);
        setIsMobileSidebarOpen(true);
      }
    });

    // Listen for group deletion
    socket.on('room:deleted', ({ roomId }) => {
      setRooms((prev) => prev.filter(room => room.roomId !== roomId));
      setMessages((prev) => {
        const newMessages = { ...prev };
        delete newMessages[roomId];
        return newMessages;
      });
      // If the deleted room was currently selected, go back to sidebar
      if (currentRoom?.roomId === roomId) {
        setCurrentRoom(null);
        setIsMobileSidebarOpen(true);
      }
    });
    
    // Store cleanup function for this socket instance
    cleanupCurrentSocket = () => {
      if (offlineHandlerRef) {
        socket.off('friend:offline', offlineHandlerRef);
      }
      socket.off('connect');
      socket.off('user:info');
      socket.off('friends:list');
      socket.off('friend:online');
      socket.off('friends:online:batch');
      socket.off('friend:bubbleColor:updated');
      socket.off('friend_request:accepted');
      socket.off('friend:removed');
      socket.off('rooms:list');
      socket.off('room:joined');
      socket.off('message:new');
      socket.off('message:edited');
      socket.off('message:deleted');
      socket.off('message:placeholder');
      socket.off('message:placeholder:removed');
      socket.off('message:placeholder:replaced');
      socket.off('message:seen');
      socket.off('message:reaction');
      socket.off('room:messages');
      socket.off('room:messages:older');
      socket.off('room:messages:around');
      socket.off('room:messages:newer');
      socket.off('room:updated');
      socket.off('room:archived');
      socket.off('room:deleted');
      socket.off('projects:list');
      socket.off('project:created');
      socket.off('project:updated');
      socket.off('project:deleted');
      socket.off('project:members:updated');
      socket.off('project:unread:counts');
      socket.off('project:unread:detail');
      socket.off('connect', requestProjectUnread);
    };
  };

    // Subscribe to socket changes - this will call setupSocketListeners
    // whenever the socket is recreated (e.g., after background/foreground)
    const unsubscribe = onSocketChange(setupSocketListeners);

    return () => {
      if (cleanupCurrentSocket) {
        cleanupCurrentSocket();
      }
      unsubscribe();
    };
  }, [playNotificationSound]);

  // ── Project handlers ──────────────────────────────────────────────
  const handleSelectProject = (project) => {
    setCurrentProject(project);
    setCurrentRoom(null); // Projects and chats are mutually exclusive views
    setIsMobileSidebarOpen(false);
  };

  // The open project's activity is on screen, so it doesn't badge; once the user leaves it,
  // the server's recount (files are marked seen on leave) decides what's left.
  const visibleProjectUnreadCounts = React.useMemo(() => {
    const openId = currentProject?.projectId;
    if (!openId || !projectUnreadCounts[openId]) return projectUnreadCounts;
    const next = { ...projectUnreadCounts };
    delete next[openId];
    return next;
  }, [projectUnreadCounts, currentProject?.projectId]);

  const handleCreateProject = (name, description, memberIds) => {
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:create', { name, description, memberIds });
    }
  };

  const handleSelectRoom = async (room, { jumpToMessageId = null } = {}) => {
    // Cancel any pending idle disconnect immediately (prevents first-click race).
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    pendingSocketUseRef.current = true;

    setCurrentRoom(room);
    setCurrentProject(null); // Projects and chats are mutually exclusive views
    setIsMobileSidebarOpen(false);

    // Show a loading state for this room until we receive room:messages
    setLoadingMessagesByRoom((prev) => ({ ...prev, [room.roomId]: true }));

    // Always load/refresh messages when selecting a room (in case messages were missed).
    // In connect-on-chat mode the socket may not exist yet right after page load.
    let socket = getSocket();
    if (!socket || !isSocketConnected()) {
      try {
        const token = await user.getIdToken();
        socket = connectSocket(token);
      } catch (_) {}
    }
    // Cancel any previous pending connect handler from a prior room switch
    if (pendingConnectHandlerRef.current && socket) {
      socket.off('connect', pendingConnectHandlerRef.current);
      pendingConnectHandlerRef.current = null;
    }

    // When opening the room at a specific message (search result / notification), load the
    // window around it instead of the newest page — otherwise the two responses race.
    const requestHistory = (reason) => {
      if (jumpToMessageId) {
        lastRoomMessagesGetAtRef.current[room.roomId] = Date.now();
        socket.emit('room:messages:get:around', { roomId: room.roomId, messageId: jumpToMessageId, limit: 25 });
      } else {
        requestRoomMessages(socket, room.roomId, { reason, force: true });
      }
    };

    if (socket && isSocketConnected()) {
      requestHistory('select-room');
    } else if (socket) {
      // If we're still connecting, emit once connected.
      const handler = () => {
        try {
          requestHistory('select-room-after-connect');
        } catch (_) {}
      };
      pendingConnectHandlerRef.current = handler;
      socket.once('connect', handler);
    }

    // Guard against the chat sticking on its loading spinner if room:messages never arrives.
    armMessagesWatchdog(room.roomId);

    // Mark all unread messages in this room as seen (when opening the chat)
    const roomMessages = messages[room.roomId] || [];
    roomMessages.forEach(msg => {
      if (msg.senderId !== user.uid && !msg.seenBy?.includes(user.uid)) {
        console.log('📖 Marking message as seen on room open:', msg.id);
        if (socket && isSocketConnected()) {
          socket.emit('message:seen', {
            roomId: room.roomId,
            messageId: msg.id
          });
        }
      }
    });
  };

  const handleLoadOlderMessages = (roomId, beforeTimestamp) => {
    const socket = getSocket();
    if (!socket || !roomId || !beforeTimestamp) return;
    setPaginationByRoom((prev) => ({
      ...prev,
      [roomId]: {
        loadingOlder: true,
        hasMoreOlder: prev[roomId]?.hasMoreOlder !== false
      }
    }));
    socket.emit('room:messages:get:older', { roomId, before: beforeTimestamp, limit: PAGE_SIZE });
  };

  // Jump to a specific message (search result, reply preview). Loads the surrounding
  // window from the server if the message isn't in the currently loaded slice, and
  // switches rooms first when the message lives in another chat.
  const handleJumpToMessage = async (roomId, messageId) => {
    if (!roomId || !messageId) return;

    const alreadyLoaded = (messages[roomId] || []).some((m) => m.id === messageId);

    if (currentRoom?.roomId !== roomId) {
      const target = rooms.find((r) => r.roomId === roomId);
      if (!target) return;
      // handleSelectRoom fetches the window around the message directly.
      await handleSelectRoom(target, { jumpToMessageId: messageId });
      return;
    }

    if (alreadyLoaded) {
      // Already on screen (or just off-screen) — ChatWindow only needs to scroll to it.
      setJumpTargetByRoom((prev) => ({ ...prev, [roomId]: { messageId, at: Date.now(), inView: true } }));
      return;
    }

    const socket = getSocket();
    if (!socket || !isSocketConnected()) return;
    setLoadingMessagesByRoom((prev) => ({ ...prev, [roomId]: true }));
    armMessagesWatchdog(roomId);
    socket.emit('room:messages:get:around', { roomId, messageId, limit: 25 });
  };

  const handleJumpHandled = (roomId) => {
    if (!roomId) return;
    setJumpTargetByRoom((prev) => {
      if (!prev[roomId]) return prev;
      const next = { ...prev };
      delete next[roomId];
      return next;
    });
  };

  // Leave a historical window and go back to the newest page.
  const handleReturnToLatest = (roomId) => {
    if (!roomId) return;
    const socket = getSocket();
    if (!socket || !isSocketConnected()) return;
    requestRoomMessages(socket, roomId, { reason: 'return-to-latest', force: true });
    armMessagesWatchdog(roomId);
  };

  const handleStartChat = (userId) => {
    const socket = getSocket();
    socket.emit('room:join:direct', { recipientId: userId });
  };

  const handleCreateGroup = (name, memberIds) => {
    const socket = getSocket();
    socket.emit('room:create:group', { name, memberIds });
  };

  const handleDeleteGroup = (roomId) => {
    if (window.confirm('Are you sure you want to delete this group? This action cannot be undone.')) {
      const socket = getSocket();
      socket.emit('room:delete:group', { roomId });
    }
  };

  const handleBackToSidebar = () => {
    setIsMobileSidebarOpen(true);
    setCurrentRoom(null);
    setCurrentProject(null);
    setActiveRoomId(null);
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
  };

  const handleOpenFriendRequests = () => {
    setShowFriendRequests(true);
  };

  const handleOpenGroupSettings = (room) => {
    setSelectedGroupForSettings(room);
    setShowGroupSettings(true);
  };

  const handleCloseGroupSettings = () => {
    setShowGroupSettings(false);
    setSelectedGroupForSettings(null);
  };

  const handleCloseFriendRequests = () => {
    setShowFriendRequests(false);
  };

  return (
    <div className="chat-container">
      <div
        className={`sidebar-wrapper ${isMobileSidebarOpen ? 'mobile-visible' : 'mobile-hidden'} ${isSidebarCollapsed ? 'desktop-collapsed' : ''}`}
      >
        <Sidebar
          user={user}
          rooms={sortedRooms}
          friends={friends}
          myBubbleColor={myBubbleColor}
          currentRoom={currentRoom}
          onSelectRoom={handleSelectRoom}
          onArchiveChat={(room) => {
            const s = getSocket();
            if (s && isSocketConnected()) s.emit('room:archive', { roomId: room.roomId, archived: true });
          }}
          onStartChat={handleStartChat}
          onCreateGroup={handleCreateGroup}
          onDeleteGroup={handleDeleteGroup}
          onOpenSettings={handleOpenSettings}
          onOpenFriendRequests={handleOpenFriendRequests}
          onOpenGroupSettings={handleOpenGroupSettings}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapsed={toggleSidebarCollapsed}
          roomUnreadCounts={roomUnreadCounts}
          sidebarTab={sidebarTab}
          onSidebarTabChange={setSidebarTab}
          projects={projects}
          currentProject={currentProject}
          onSelectProject={handleSelectProject}
          onCreateProject={handleCreateProject}
          projectUnreadCounts={visibleProjectUnreadCounts}
        />
      </div>
      <div className={`chat-wrapper ${!isMobileSidebarOpen ? 'mobile-visible' : 'mobile-hidden'}`}>
        {currentProject ? (
          <ProjectView
            user={user}
            project={currentProject}
            friends={friends}
            myBubbleColor={myBubbleColor}
            onBack={handleBackToSidebar}
            messages={messages}
            setMessages={setMessages}
            loadingMessagesByRoom={loadingMessagesByRoom}
            paginationByRoom={paginationByRoom}
            onLoadOlderMessages={handleLoadOlderMessages}
            unreadDetail={projectUnreadDetail[currentProject.projectId]}
          />
        ) : (
          <ChatWindow
            user={user}
            room={currentRoom}
            messages={messages[currentRoom?.roomId] || []}
            isLoadingMessages={!!loadingMessagesByRoom[currentRoom?.roomId]}
            friends={friends}
            myBubbleColor={myBubbleColor}
            onBack={handleBackToSidebar}
            onLoadOlderMessages={handleLoadOlderMessages}
            loadingOlder={!!paginationByRoom[currentRoom?.roomId]?.loadingOlder}
            hasMoreOlder={paginationByRoom[currentRoom?.roomId]?.hasMoreOlder !== false}
            hasMoreNewer={!!paginationByRoom[currentRoom?.roomId]?.hasMoreNewer}
            jumpTarget={jumpTargetByRoom[currentRoom?.roomId] || null}
            onJumpToMessage={handleJumpToMessage}
            onJumpHandled={handleJumpHandled}
            onReturnToLatest={handleReturnToLatest}
          />
        )}
      </div>
      
      {showSettings && (
        <Settings
          user={user}
          myBubbleColor={myBubbleColor}
          onBubbleColorChange={setMyBubbleColor}
          onClose={handleCloseSettings}
          archivedRooms={rooms.filter(r => r.archived)}
          onOpenArchivedChat={(room) => {
            handleSelectRoom(room);
            handleCloseSettings();
          }}
          onUnarchiveChat={(room) => {
            const s = getSocket();
            if (s && isSocketConnected()) s.emit('room:archive', { roomId: room.roomId, archived: false });
          }}
        />
      )}

      {showFriendRequests && (
        <FriendRequests
          user={user}
          onClose={handleCloseFriendRequests}
        />
      )}

      {showGroupSettings && selectedGroupForSettings && (
        <GroupSettings
          room={selectedGroupForSettings}
          user={user}
          onClose={handleCloseGroupSettings}
        />
      )}
    </div>
  );
}

export default Chat;

